'use client'

import { setTermsOfUseAcceptedCookie, TERMS_OF_USE_COOKIE, TermsOfUseNotice } from '@repo/ui'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore
} from 'react'

const TermsOfUseContext = createContext<{ accepted: boolean }>({ accepted: true })

/** Whether the visitor has accepted the Terms of Use notice. */
export const useTermsOfUseAccepted = () => useContext(TermsOfUseContext).accepted

// The Safe app and partner embeds load vaults in a cross-site iframe that drops the cookie,
// so acceptance is mirrored to localStorage.
function readStoredAcceptance(): boolean {
  try {
    return localStorage.getItem(TERMS_OF_USE_COOKIE) === 'true'
  } catch {
    return false
  }
}

const subscribeToNothing = () => () => {}

function storeAcceptance(): void {
  try {
    localStorage.setItem(TERMS_OF_USE_COOKIE, 'true')
  } catch {
    // Storage unavailable: the cookie is the only record.
  }
}

type TermsOfUseProviderProps = PropsWithChildren<{ accepted: boolean }>

/**
 * Renders the shared Terms of Use notice and exposes its accepted state, so on-mount dialogs
 * can wait until the notice is gone instead of portalling above it.
 */
export const TermsOfUseProvider = ({
  accepted: cookieAccepted,
  children
}: TermsOfUseProviderProps) => {
  const storedAccepted = useSyncExternalStore(subscribeToNothing, readStoredAcceptance, () => false)
  const [acceptedNow, setAcceptedNow] = useState(false)
  const accepted = cookieAccepted || storedAccepted || acceptedNow

  // Restore an expired or cleared cookie so the server stops rendering the notice.
  useEffect(() => {
    if (storedAccepted && !cookieAccepted) setTermsOfUseAcceptedCookie()
  }, [storedAccepted, cookieAccepted])

  function onAccept() {
    storeAcceptance()
    setAcceptedNow(true)
  }

  return (
    <TermsOfUseContext.Provider value={{ accepted }}>
      {children}
      {accepted ? null : <TermsOfUseNotice accepted={false} onAccept={onAccept} />}
    </TermsOfUseContext.Provider>
  )
}
